import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { CreateSessionModal } from './index-components.tsx'
import type { AgentOptionType, CollectSessionType, DeviceOptionType, PaginationType } from './types'
import { Activity, Clock, Eye, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Badge } from "@/components/ui/badge.tsx"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table.tsx"
import Pagination from "@/components/pagination.tsx"

interface IndexProps {
  collect_sessions: CollectSessionType[]
  agents?: AgentOptionType[]
  devices?: DeviceOptionType[]
  filters?: {
    term: string
  }
  pagination?: PaginationType
  can_create?: boolean
  can_destroy?: boolean
}

export default function Index({
  collect_sessions,
  agents = [],
  devices = [],
  filters = { term: "" },
  pagination,
  can_create = false,
  can_destroy = false
}: IndexProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const hasInitialized = useRef(false)
  const [term, setTerm] = useState(filters.term)

  useEffect(() => {
    setTerm(filters.term)
  }, [filters.term])

  const query = useMemo(() => ({ term }), [term])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      return
    }

    if (term === filters.term) {
      return
    }

    const timeoutId = setTimeout(() => {
      router.get("/collect_sessions", { ...query }, {
        preserveState: true,
        preserveScroll: true,
        replace: true
      })
    }, 350)

    return () => clearTimeout(timeoutId)
  }, [query, term, filters.term])

  const formatDateTime = (value?: string | null) => {
    if (!value) return "-"

    return new Date(value).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const formatAmount = (value?: number) => `${Number(value || 0).toFixed(2)} FCFA`

  const formatAreas = (collection: CollectSessionType) => {
    if (!collection.areas || collection.areas.length === 0) return "-"

    return collection.areas.map((area) => area.name).join(", ")
  }

  const statusLabel = (status: string) => {
    switch (status) {
      case "open":
        return "Ouverte"
      case "closed":
        return "Fermée"
      case "reconciled":
        return "Réconciliée"
      default:
        return status
    }
  }

  const statusClassName = (status: string) => {
    switch (status) {
      case "open":
        return "bg-success/10 text-success"
      case "closed":
        return "bg-warning/10 text-warning"
      case "reconciled":
        return "bg-primary/10 text-primary"
      default:
        return undefined
    }
  }

  const handleDestroy = (collection: CollectSessionType) => {
    if (!window.confirm(`Supprimer la session "${collection.code}" ?`)) return

    router.delete(`/collect_sessions/${collection.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>
      <Head title="Sessions de collecte" />
      <div className="p-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-foreground">Les sessions de collecte</h2>
            <p className="text-muted-foreground">Gérer les différentes sessions de collecte</p>
          </div>
          <div className="flex gap-2">
            {can_create && (
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 rounded-lg border border-border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Clock className="h-4 w-4" />
                Nouvelle session
              </button>
            )}
            <Link href="/admin/devices">
              <button className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-secondary">
                <Activity className="h-4 w-4" />
                Les terminaux
              </button>
            </Link>

          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Rechercher par agent, terminal ou zone..."
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Session</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Terminal</TableHead>
                <TableHead>Zones</TableHead>
                <TableHead>Début</TableHead>
                <TableHead>Fin</TableHead>
                <TableHead className="text-right">Total collecté</TableHead>
                <TableHead className="text-right">Paiements</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collect_sessions.length > 0 ? (
                collect_sessions.map((collection) => (
                  <TableRow key={collection.id}>
                    <TableCell>
                      <Link
                        href={`/collect_sessions/${collection.id}`}
                        className="font-semibold text-foreground hover:text-primary"
                      >
                        {collection.code}
                      </Link>
                    </TableCell>
                    <TableCell>{collection.agent_name || collection.user_id}</TableCell>
                    <TableCell>{collection.device_name || collection.device_id}</TableCell>
                    <TableCell className="max-w-xs whitespace-normal text-muted-foreground">
                      {formatAreas(collection)}
                    </TableCell>
                    <TableCell>{formatDateTime(collection.start_at)}</TableCell>
                    <TableCell>{formatDateTime(collection.end_at)}</TableCell>
                    <TableCell className="text-right font-semibold text-success">
                      {formatAmount(collection.total_collected_amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {collection.total_payments_count || 0}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={statusClassName(collection.status) ? "default" : "secondary"}
                        className={statusClassName(collection.status)}
                      >
                        {statusLabel(collection.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon-sm" aria-label={`Voir ${collection.code}`}>
                        <Link href={`/collect_sessions/${collection.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                      {can_destroy && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={`Supprimer ${collection.code}`}
                          onClick={() => handleDestroy(collection)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} className="h-24 text-center text-muted-foreground">
                    Aucune session de collecte trouvée.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {pagination && (
          <div className="mt-4">
            <Pagination
              currentPage={pagination.current_page}
              totalPages={pagination.total_pages}
              basePath="/collect_sessions"
              query={query}
            />
          </div>
        )}

      </div>

      {isCreateModalOpen && can_create && (
        <CreateSessionModal
          agents={agents}
          devices={devices}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </>
  )
}
